/**
 * Núcleo PURO do PDV: **zero import de zod**, e isso é o ponto do arquivo.
 *
 * `@vertex/validators` é consumido como código-fonte, então importar qualquer
 * coisa de `pdv.ts` no cliente arrasta o zod inteiro (~60 KB gz) — mais de um
 * quarto do orçamento de 200 KB do terminal, para validar o que o servidor já
 * valida com `ZodBodyPipe`. O `apps/pdv` importa SÓ daqui.
 *
 * Nada disto é duplicata: `commerce.ts` e `pdv.ts` passaram a importar deste
 * arquivo, então a aritmética de venda tem uma fonte só. Servidor e tela
 * arredondam pelo MESMO código — é o que garante que o total exibido é o total
 * cobrado.
 *
 * Regra pra quem mexer aqui: se precisar de `z.` , o lugar é `pdv.ts`.
 */

/* Atalhos: mesmo contrato, mesma ausência de zod. Reexportados daqui pro
   terminal ter um import só (`@vertex/validators/pdv-pure`). */
export * from "./pdv-shortcuts";

/* A conta da maquininha mora aqui pelo MESMO motivo: o terminal precisa dela
   para mostrar quanto o cliente paga, e ter duas implementações da mesma conta
   é o que fazia tela e servidor discordarem em um centavo. */
export * from "./card-fees-pure";
import type { CardFeeMode, CardModality } from "./card-fees-pure";

/* "Promoção só vale quando é promoção" pela MESMA função do resto do sistema:
   `promo_price = 0` já mandou item a R$ 0,00 pro caixa. */
export * from "./preco-pure";
import { precoDeVenda } from "./preco-pure";

// ─────────────────────────── Unidades de venda ───────────────────────────

/**
 * Unidade de venda (e fiscal) do item. É como o preço é lido ("R$ 29,90 por
 * kg") — a MESMA coluna de `products.unit` / `service_catalog.unit`.
 *
 * `base` é a unidade menor da mesma grandeza e `perBase` quanto dela cabe em 1
 * unidade de venda: é o que permite digitar 350 g numa venda por kg
 * (350 ÷ 1000 = 0,350 kg) sem espalhar conversão pela tela.
 */
export const SALE_UNITS = {
  UN: { label: "Unidade", short: "un", kind: "count", decimals: 0, base: null, perBase: 1 },
  KG: { label: "Quilo", short: "kg", kind: "weight", decimals: 3, base: "G", perBase: 1000 },
  G: { label: "Grama", short: "g", kind: "weight", decimals: 0, base: null, perBase: 1 },
  L: { label: "Litro", short: "L", kind: "volume", decimals: 3, base: "ML", perBase: 1000 },
  ML: { label: "Mililitro", short: "mL", kind: "volume", decimals: 0, base: null, perBase: 1 },
  M: { label: "Metro", short: "m", kind: "length", decimals: 3, base: "CM", perBase: 100 },
  CM: { label: "Centímetro", short: "cm", kind: "length", decimals: 0, base: null, perBase: 1 },
} as const;

export type SaleUnit = keyof typeof SALE_UNITS;

/** Códigos na ordem canônica. `pdv.ts` monta o `z.enum` a partir daqui. */
export const SALE_UNIT_CODES = ["UN", "KG", "G", "L", "ML", "M", "CM"] as const;

/**
 * Como a unidade gravada se lê na venda.
 *
 * `products.unit` também é a unidade FISCAL (o `uCom` da nota), e o cadastro
 * importado de outro sistema chega com a grafia da nota: "MT", "PC", "PÇ",
 * "LT". O Beto Borrachas tinha 242 mangueiras em "MT" e o balcão vendia por
 * unidade (30/10): "MT" não era "M", então não era metro. E o contrário:
 * "PC" não era "UN", então peça aceitava 1,5.
 *
 * A gravação não muda (a nota segue saindo com "MT"); só a leitura da venda
 * passa por aqui. O que não é peso, volume ou comprimento é contado.
 */
const APELIDOS_DE_UNIDADE: Record<string, SaleUnit> = {
  MT: "M",
  MTS: "M",
  MTR: "M",
  METRO: "M",
  METROS: "M",
  KGS: "KG",
  QUILO: "KG",
  GR: "G",
  GRS: "G",
  LT: "L",
  LTS: "L",
  LITRO: "L",
  CMS: "CM",
};

export function saleUnitOf(unit: string | null | undefined): SaleUnit {
  const u = (unit ?? "UN").trim().toUpperCase();
  if (u in SALE_UNITS) return u as SaleUnit;
  return APELIDOS_DE_UNIDADE[u] ?? "UN";
}

/** Só a unidade contada é indivisível; peso/volume/metragem aceitam casa decimal. */
export function isFractionalUnit(unit: string | null | undefined): boolean {
  return saleUnitOf(unit) !== "UN";
}

/** Casas decimais da unidade (0 para unidade contada). */
export function unitDecimals(unit: string | null | undefined): number {
  return SALE_UNITS[saleUnitOf(unit)].decimals;
}

/** Rótulo curto pra colar no número: "2 un" · "0,350 kg". */
export function unitShort(unit: string | null | undefined): string {
  return SALE_UNITS[saleUnitOf(unit)].short;
}

// ───────────────────────────── Aritmética ─────────────────────────────
/* Mora em `money-pure.ts`, que não importa nada: é a folha da árvore. A
   separação desfez o ciclo `pdv-pure ↔ card-fees-pure`, que se resolvia por
   ordem de importação e quebrou nove testes no dia em que a ordem mudou. Os
   nomes continuam saindo daqui, então nenhum import precisou mudar. */
export { lineTotal, roundMoney, roundQty } from "./money-pure";
/* Só o que ESTE arquivo usa; a reexportação acima é que serve quem importa. */
import { roundMoney, roundQty } from "./money-pure";

/**
 * A unidade manda na quantidade: item contado (`UN`) não vende 1,5; item por
 * peso/volume/metragem vende. Devolve a mensagem de erro ou null.
 */
export function qtyErrorForUnit(qty: number, unit: string | null | undefined): string | null {
  if (!(qty > 0)) return "Quantidade deve ser maior que zero";
  if (!isFractionalUnit(unit) && !Number.isInteger(roundQty(qty))) {
    return "Este item é vendido por unidade: use quantidade inteira";
  }
  return null;
}

// ──────────────────────── Constantes do terminal ────────────────────────

/**
 * Atalhos da tela do terminal. As chaves são contrato com o operador: mudar
 * uma aqui esconde o tile de quem já configurou, então chave nova só se
 * ADICIONA, nunca se renomeia.
 */
export const PDV_TILES = [
  "desconto",
  "estoque",
  "cliente",
  "vendas",
  "sangria",
  "suprimento",
  "turno",
  "fechar",
  "os",
  "retiradas",
] as const;
export type PdvTile = (typeof PDV_TILES)[number];

/** Formas de pagamento configuráveis por org (`pdv_settings.paymentMethods`). */
export const PDV_PAYMENT_METHODS = ["cash", "pix", "card", "boleto", "voucher"] as const;
export type PdvPaymentMethod = (typeof PDV_PAYMENT_METHODS)[number];

/** O que o CHECKOUT aceita — subconjunto do acima (fiado ficou de fora). */
export const PDV_CHECKOUT_METHODS = ["cash", "pix", "card"] as const;
export type PdvCheckoutMethod = (typeof PDV_CHECKOUT_METHODS)[number];

export const PDV_ACCESS_MODES = ["web", "web_app"] as const;
export type PdvAccessMode = (typeof PDV_ACCESS_MODES)[number];

/**
 * Quando o cliente LEVA. Eixo independente de quando PAGA: 'now' sai hoje,
 * 'later' fica na loja e ele volta buscar.
 */
export const PDV_FULFILLMENTS = ["now", "later"] as const;
export type PdvFulfillment = (typeof PDV_FULFILLMENTS)[number];

/** Como se paga o que FICA na loja. */
export const PDV_PAYMENT_MODES = ["now", "on_pickup"] as const;
export type PdvPaymentMode = (typeof PDV_PAYMENT_MODES)[number];

export const PDV_ITEM_KINDS = ["product", "service", "digital"] as const;
export type PdvItemKind = (typeof PDV_ITEM_KINDS)[number];

/**
 * Teto padrão de horas que o terminal aceita operar sem falar com o servidor
 * (`pdv_settings.offline_grace_hours`). Passado o prazo ele trava venda nova e
 * só permite sincronizar e fechar. Aviso visual a partir de `PDV_OFFLINE_WARN_HOURS`.
 */
export const PDV_OFFLINE_GRACE_HOURS_DEFAULT = 48;
export const PDV_OFFLINE_WARN_HOURS = 24;

// ────────────────── Formatação e parsing (balcão brasileiro) ──────────────────

/** R$ com 2 casas, pt-BR. */
export function brl(v: number): string {
  return Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

/**
 * Lê dinheiro digitado no balcão. Aceita "29,90", "R$ 29,90", "1.299,90" e
 * "29.90" — o operador digita dos dois jeitos e nenhum pode virar NaN.
 *
 * A regra é: se tem vírgula, a vírgula é o decimal e o ponto é milhar; se só
 * tem ponto, o ponto é o decimal.
 */
export function parseMoney(raw: string): number {
  const s = String(raw ?? "").replace(/[^\d.,-]/g, "");
  if (!s) return 0;
  const normalized = s.includes(",") ? s.replace(/\./g, "").replace(",", ".") : s;
  const n = Number(normalized);
  return Number.isFinite(n) ? roundMoney(n) : 0;
}

/**
 * O padrão `3*789` do balcão: quantidade, asterisco, código. É como o operador
 * de supermercado digita "três daquele" sem tirar a mão do teclado numérico.
 * Sem o asterisco, tudo é código.
 */
export function splitQtyPrefix(input: string): { qty: number; code: string } {
  const m = /^\s*(\d+(?:[.,]\d+)?)\s*[*xX]\s*(.+)$/.exec(input ?? "");
  if (!m) return { qty: 1, code: (input ?? "").trim() };
  const qty = roundQty(Number(m[1]!.replace(",", ".")));
  return { qty: qty > 0 ? qty : 1, code: m[2]!.trim() };
}

/**
 * Notas que o caixa provavelmente recebeu, pra virar botão de atalho no
 * pagamento em dinheiro: o valor exato mais os arredondamentos plausíveis
 * acima dele. Só cédula do real, e nunca sugestão menor que o total.
 */
const CEDULAS = [2, 5, 10, 20, 50, 100, 200];
export function sugestoesDeNotas(total: number, max = 4): number[] {
  const alvo = roundMoney(total);
  if (!(alvo > 0)) return [];
  const out = new Set<number>([alvo]);
  for (const c of CEDULAS) {
    const acima = Math.ceil(alvo / c) * c;
    if (acima >= alvo) out.add(roundMoney(acima));
  }
  return [...out].sort((a, b) => a - b).slice(0, max);
}

/**
 * Diz se um fundo hex é escuro, pra escolher texto branco ou preto por cima.
 * Usa luminância percebida (ITU-R BT.601), não a média dos canais: um verde
 * puro e um azul puro têm a mesma média e brilhos muito diferentes.
 */
export function corEscura(hex: string | null | undefined): boolean {
  const h = String(hex ?? "").replace("#", "");
  if (h.length !== 6) return true;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  if ([r, g, b].some((n) => Number.isNaN(n))) return true;
  return (r * 299 + g * 587 + b * 114) / 1000 < 140;
}

// ───────────────────────────── Parcelamento ─────────────────────────────

/**
 * Até quantas vezes esta venda pode parcelar. O limite real é o MENOR entre o
 * teto da org e o que a parcela mínima permite: uma loja com teto de 12× e
 * parcela mínima de R$ 50 não parcela R$ 120 em 12 (daria R$ 10).
 */
export function maxParcelas(total: number, tetoOrg: number, parcelaMinima: number | null): number {
  const teto = Math.max(1, Math.floor(tetoOrg || 1));
  if (!parcelaMinima || parcelaMinima <= 0) return teto;
  const cabe = Math.floor(roundMoney(total) / parcelaMinima);
  return Math.min(teto, Math.max(1, cabe));
}

/**
 * Quebra o total em N parcelas fechando o centavo: as primeiras absorvem o
 * resto da divisão. Somar as parcelas SEMPRE devolve o total — é isso que
 * impede a maquininha e o comprovante de discordarem em um centavo.
 */
export function parcelas(total: number, n: number): number[] {
  const qtd = Math.max(1, Math.floor(n || 1));
  const centavos = Math.round(roundMoney(total) * 100);
  const base = Math.floor(centavos / qtd);
  const resto = centavos - base * qtd;
  return Array.from({ length: qtd }, (_, i) => (base + (i < resto ? 1 : 0)) / 100);
}

// ──────────────────────── Busca local (espelho SQLite) ────────────────────────

/**
 * Normaliza texto pra busca: minúsculas sem acento. Espelha o `norm()` do
 * `pdv-catalog.service.ts` — o `search_blob` do espelho local é gravado com
 * ESTA função, então divergir aqui quebra a busca offline em silêncio.
 */
export function norm(s: string | null | undefined): string {
  return String(s ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

/** Versão sem separadores, pra casar código digitado com hífen/espaço. */
export function compact(s: string | null | undefined): string {
  return norm(s).replace(/[^a-z0-9]/g, "");
}

// ──────────────────────────── Tipos do terminal ────────────────────────────
/* Escritos à mão em vez de inferidos do zod: inferir arrastaria o zod junto. */

export interface PdvCartLine {
  kind: PdvItemKind;
  /** Produto vende pela VARIANTE; serviço/digital pela referência do item. */
  variantId?: string;
  refId?: string;
  qty: number;
  unitPrice?: number;
  fulfillment: PdvFulfillment;
}

/** Maquininha usada na venda. Espelha o `cardPaymentSchema` do `card-machines.ts`. */
export interface PdvCardPayment {
  machineId: string;
  modality: CardModality;
  installments?: number;
  /** true = o cliente paga a taxa (gross-up). O servidor decide se pode. */
  passFeeToCustomer?: boolean;
  /** O valor que a tela mostrou. Só para conferência: não entra no cálculo. */
  previewCharged?: number;
  /** Taxa informada só para esta venda. Ausente = a do cadastro. */
  feePercent?: number;
  feeFixed?: number;
}

export interface PdvCheckoutBody {
  /** uuid da mutação, reenviado igual em todo retry. Vira `outbox.id` na fila. */
  clientRef?: string;
  items: PdvCartLine[];
  paymentMode: PdvPaymentMode;
  discount: number;
  paymentMethod: PdvCheckoutMethod;
  card?: PdvCardPayment;
  customerContactId?: string;
  emitNfe?: boolean;
  /** Cupom (NFC-e) ou nota (NF-e) . escolha do operador, não dedução. */
  docType?: "nfe" | "nfce" | "nfse";
  /** Quem vendeu (usuário da org), quando o terminal pergunta. */
  sellerUserId?: string;
  pickupDueAt?: string;
  /** Venda a prazo pra cliente com atraso, liberada por quem pode (29/09). */
  liberarAtraso?: boolean;
  priceOverride?: {
    authorizedByUserId?: string | null;
    authorizedByName: string;
    reason: string;
  };
}

/** Resposta do checkout. Espelha o `CheckoutResult` do `pdv-checkout.service`. */
export interface PdvCheckoutResult {
  order: { id: string; number: string; docNumber: string | null; total: number } | null;
  pickupOrder: { id: string; number: string; docNumber: string | null; total: number } | null;
  serviceOrders: Array<{ id: string; number: string; total: number; paid: boolean }>;
  paidNow: number;
  dueOnPickup: number;
  cashSessionId: string | null;
  /**
   * Taxa da maquininha desta venda. Os campos antigos (`feeAmount`, `net`,
   * `installments`, `firstDueAt`, `semRegra`) mantêm nome e sentido: terminal
   * antigo continua lendo o que sempre leu.
   */
  cardFee: {
    feeAmount: number;
    net: number;
    installments: number;
    firstDueAt: string;
    semRegra: boolean;
    /** Quem pagou a taxa nesta venda. */
    mode: CardFeeMode;
    /** Valor combinado da venda, antes do repasse. */
    valorVenda: number;
    /** O que o cliente pagou. Em `absorb` é igual a `valorVenda`. */
    valorCobrado: number;
    acrescimo: number;
    /** Repasse pedido mas não aplicado (taxa >= 100%, ou venda com OS). */
    repasseRecusado: boolean;
    /** O `previewCharged` do terminal não bateu com o recálculo do servidor. */
    previewMismatch: boolean;
  } | null;
  /** `true` quando a idempotência devolveu o resultado já gravado. */
  replayed?: boolean;
}

/** Linha do catálogo como o `/products/search` e o espelho local devolvem. */
export interface PdvVariant {
  id: string;
  sku: string;
  barcode: string | null;
  name: string | null;
  productName: string;
  price: number;
  promoPrice: number | null;
  minPrice: number | null;
  unit: string | null;
  isCombo: boolean;
  trackStock: boolean;
  /** Sob encomenda: o saldo continua valendo, mas zerado não impede a venda. */
  allowBackorder: boolean;
  stock: number | null;
  image: string | null;
}

/** Preço que vale agora: promoção quando ela é promoção (ver `preco-pure`). */
export function precoEfetivo(v: Pick<PdvVariant, "price" | "promoPrice">): number {
  return roundMoney(precoDeVenda(v));
}

/** Estado do caixa como o `/context` e o `/register/current` devolvem. */
export interface PdvRegisterState {
  open: boolean;
  sessionId?: string;
  openedAt?: string;
  openingFloat?: number;
  expectedCash?: number;
  salesTotal?: number;
}
